
interface ReviewProps {
  author: string;
  reviewBody: string;
  datePublished: string;
  rating?: number;
}

interface ItemReviewedProps {
  name: string;
  // Updated to only include Google-supported types for reviews
  type: "LocalBusiness" | "Product" | "Course" | "Event" | "Movie" | "Recipe" | "SoftwareApplication" | "Book" | "HowTo";
  url?: string;
  identifier?: string;
}

/**
 * Generate Review schema for testimonials with proper structure
 * for Google structured data validation
 * @param reviews Array of reviews
 * @param itemReviewed The reviewed entity (must be one of the Google-supported types)
 */
export function generateReviewSchema(reviews: ReviewProps[], itemReviewed: ItemReviewedProps) {
  // Ensure URL is absolute
  const itemUrl = itemReviewed.url && !itemReviewed.url.startsWith('http') 
    ? `https://www.grandpal.co${itemReviewed.url}`
    : itemReviewed.url;

  // Calculate aggregate rating if ratings are available
  const ratingsAvailable = reviews.some(review => review.rating !== undefined);
  let aggregateRating = null;
  
  if (ratingsAvailable) {
    const validRatings = reviews.filter(r => r.rating !== undefined).map(r => r.rating as number);
    const ratingSum = validRatings.reduce((sum, rating) => sum + rating, 0);
    const ratingCount = validRatings.length;
    const ratingAverage = ratingSum / ratingCount;
    
    aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": ratingAverage.toFixed(1),
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": ratingCount
    };
  }

  // Generate unique identifier for the item
  const uniqueId = itemReviewed.identifier || 
    (itemUrl ? itemUrl : `https://www.grandpal.co/item/${itemReviewed.name.toLowerCase().replace(/\s+/g, '-')}`);

  // Prepare the schema base
  const schemaBase = {
    "@context": "https://schema.org",
    "@type": itemReviewed.type,
    "@id": uniqueId,
    "name": itemReviewed.name,
    ...(itemUrl && { "url": itemUrl }),
    ...(aggregateRating && { "aggregateRating": aggregateRating })
  };

  // Only add reviews array if reviews exist
  if (reviews.length > 0) {
    const reviewsSchema = reviews.map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.author
      },
      "reviewBody": review.reviewBody,
      "datePublished": review.datePublished,
      ...(review.rating && {
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": review.rating,
          "bestRating": "5",
          "worstRating": "1"
        }
      })
    }));

    return {
      ...schemaBase,
      "review": reviewsSchema
    };
  }

  return schemaBase;
}
